import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { SubProject } from 'src/app/model/project/sub-project';
import RegisteredAction from 'src/app/model/registered-action';
import RegisteredActionType from 'src/app/model/registered-action-type';
import { User } from 'src/app/model/user';
import { _post } from '../../base.service';
import { ImmoIPProductChangesObject } from '../../immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes-object';
import { ImmoIPNotExecutedHistoryItemObject } from '../../immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-not-executed-history-item-object';
import { ImmoIPSubProjectChangesObject } from '../../immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes-object';
import { LoadingService } from '../../loading.service';
import { SnackService } from '../../snack.service';

@Injectable({
  providedIn: 'root',
})
export class TourInstallerSubmitSubProjectControllerRequestService {
  private static BASE_URL = '/tourInstaller';
  private readonly post: <T>(
    path: string,
    body: any,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly deserialize = (res) => res as Response;
  private readonly deserializeSubProject = (res) => res as SubProject;

  constructor(
    http: HttpClient,
    snackService: SnackService,
    loadingService: LoadingService,
    router: Router
  ) {
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.post = _post(
      TourInstallerSubmitSubProjectControllerRequestService.BASE_URL,
      requestServices
    );
  }

  public submitSubProject(
    productChangesArr: ImmoIPProductChangesObject[],
    subProjectChanges: ImmoIPSubProjectChangesObject
  ): Promise<Response> {
    const requestBody: RequestBody = this.constructRequestBody(
      productChangesArr,
      subProjectChanges
    );
    return this.post(
      '/submitSubProject',
      requestBody,
      this.deserialize
    ).toPromise();
  }

  public submitChanges(
    productChangesArr: ImmoIPProductChangesObject[],
    subProjectChanges: ImmoIPSubProjectChangesObject
  ): Promise<Response> {
    const requestBody: RequestBody = this.constructRequestBody(
      productChangesArr,
      subProjectChanges
    );
    return this.post(
      '/submitChanges',
      requestBody,
      this.deserialize
    ).toPromise();
  }

  public submitInternalInfoChanges(
    subProjectChanges: ImmoIPSubProjectChangesObject
  ): Promise<Response> {
    const requestBody = {
      id: subProjectChanges.id,
      internalInfo: subProjectChanges.internalInfo,
      recurringInternalInfo: subProjectChanges.recurringInternalInfo,
    };
    return this.post(
      '/submitInternalInfoChanges',
      requestBody,
      this.deserialize
    ).toPromise();
  }

  public submitModifyPlacementConfig(
    subProjectChanges: ImmoIPSubProjectChangesObject
  ): Promise<SubProject> {
    const requestBody = {
      subProjectId: subProjectChanges.id,
      boardType: subProjectChanges.boardType,
      placementType: subProjectChanges.placementType,
    };
    return this.post(
      '/modifyPlacementConfig',
      requestBody,
      this.deserializeSubProject
    ).toPromise();
  }

  public submitModifyAddress(
    subProjectChanges: ImmoIPSubProjectChangesObject
  ): Promise<SubProject> {
    const requestBody = {
      subProjectId: subProjectChanges.id,
      address: subProjectChanges.address,
    };
    return this.post(
      '/modifyAddress',
      requestBody,
      this.deserializeSubProject
    ).toPromise();
  }

  private constructRequestBody(
    productChangerArr: ImmoIPProductChangesObject[],
    subProjectChanges: ImmoIPSubProjectChangesObject
  ): RequestBody {
    return {
      products:
        productChangerArr == null
          ? null
          : productChangerArr.map((p) => {
              return {
                id: p.id,
                installerFeedback: p.installerFeedback,
                executionComplete: p.executionComplete,
                executionDate: p.executionDate,
                executionTime: p.executionTime,
              };
            }),
      subProject: {
        id: subProjectChanges.id,
        internalInfo: subProjectChanges.internalInfo,
        recurringInternalInfo: subProjectChanges.recurringInternalInfo,
        placerFeedback: subProjectChanges.placerFeedback,
        registeredActions: this.constructRequestRegisteredActions(
          subProjectChanges.registeredActions
        ),
        placementDone: subProjectChanges.placementDone,
        currentExecutionDate: subProjectChanges.currentExecutionDate,
      },
      notExecutedHistoryItem: this.constructRequestNotExecutedHistoryitem(
        subProjectChanges.notExecutedHistoryItemObject
      ),
    };
  }

  private constructRequestRegisteredActions(
    registeredActions: RegisteredAction[]
  ): RequestRegisteredAction[] {
    return registeredActions == null
      ? null
      : registeredActions.map((ra) => {
          return {
            id: ra.id,
            user: ra.user,
            date: ra.date,
            type: ra.type,
          };
        });
  }

  private constructRequestNotExecutedHistoryitem(
    notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject
  ): RequestNotExecutedHistoryItem {
    return notExecutedHistoryItemObject == null
      ? null
      : {
          content: notExecutedHistoryItemObject.content,
          callClient: notExecutedHistoryItemObject.callClient,
          clientCommunicationSummary:
            notExecutedHistoryItemObject.clientCommunicationSummary,
          callInpress: notExecutedHistoryItemObject.callInpress,
          inpressCommunicationSummary:
            notExecutedHistoryItemObject.inpressCommunicationSummary,
          suggestedFollowUp: notExecutedHistoryItemObject.suggestedFollowUp,
          stockAtBranchId: notExecutedHistoryItemObject.stockAtBranchId,
          id: notExecutedHistoryItemObject.id,
        };
  }
}

interface RequestBody {
  products: RequestProduct[];
  subProject: RequestSubProject;
  notExecutedHistoryItem: RequestNotExecutedHistoryItem;
}

interface RequestProduct {
  id: number;
  installerFeedback: string;
  executionComplete: boolean;
  executionDate: Date;
  executionTime: string;
}

interface RequestSubProject {
  id: number;
  internalInfo: string;
  recurringInternalInfo: boolean;
  placerFeedback: string;
  registeredActions: RequestRegisteredAction[];
  placementDone: boolean;
  currentExecutionDate: Date;
}

interface RequestRegisteredAction {
  id: number;
  user: User;
  date: Date;
  type: RegisteredActionType;
}

interface RequestNotExecutedHistoryItem {
  content: string;
  callClient: boolean;
  clientCommunicationSummary: string;
  callInpress: boolean;
  inpressCommunicationSummary: string;
  suggestedFollowUp: string;
  stockAtBranchId: number;
  id: number;
}

export interface Response {
  subProjectStatus: string;
  registeredActions: RegisteredAction[];
  notExecutedHistoryItemId: number;
}
